import { default_banner } from "@/utils/config";

const SEO_SITE_NAME = "立吉答题";
const SEO_SITE_ORIGIN = "https://m.xiaoxiaodati.com";
const SEO_DEFAULT_TITLE = "立吉答题丨在线考试刷题-企业培训考核";
const SEO_DEFAULT_DESCRIPTION =
  "立吉答题，让在线学、练、考更容易。支持线上考试、企业培训、问卷调查、知识竞赛、有奖答题、每日一练、答题卡等场景，1分钟快速创建。";
const SEO_DEFAULT_KEYWORDS =
  "立吉答题,在线考试刷题,企业培训考核,线上考试,问卷调查,知识竞赛,有奖答题,每日一练,答题卡";
const SEO_MANAGED_ATTR = "data-seo-managed";
const SEO_JSONLD_ID = "app-seo-jsonld";

const META_DEFINITIONS = [
  { key: "description", attr: "name", value: "description" },
  { key: "keywords", attr: "name", value: "keywords" },
  { key: "robots", attr: "name", value: "robots" },
  { key: "og:site_name", attr: "property", value: "og:site_name" },
  { key: "og:locale", attr: "property", value: "og:locale" },
  { key: "og:type", attr: "property", value: "og:type" },
  { key: "og:title", attr: "property", value: "og:title" },
  { key: "og:description", attr: "property", value: "og:description" },
  { key: "og:url", attr: "property", value: "og:url" },
  { key: "og:image", attr: "property", value: "og:image" },
  { key: "twitter:card", attr: "name", value: "twitter:card" },
  { key: "twitter:title", attr: "name", value: "twitter:title" },
  { key: "twitter:description", attr: "name", value: "twitter:description" },
  { key: "twitter:image", attr: "name", value: "twitter:image" }
];

function getSiteBasePath() {
  if (typeof window === "undefined") {
    return "";
  }
  const path = window.location.pathname || "";
  return path === "/test" || path.indexOf("/test/") === 0 ? "/test" : "";
}

function getBaseUrl() {
  return SEO_SITE_ORIGIN + getSiteBasePath();
}

function getDefaultImage() {
  return toAbsoluteUrl(default_banner);
}

function trimText(value) {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function limitText(value, maxLength) {
  const text = trimText(value);
  if (!text || text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength - 1).trim() + "…";
}

function toAbsoluteUrl(url) {
  const value = trimText(url);
  if (!value) {
    return "";
  }
  if (/^https?:\/\//i.test(value)) {
    return value;
  }
  if (value.indexOf("//") === 0) {
    return "https:" + value;
  }
  const normalized = value.replace(/^\.\//, "");
  if (normalized.charAt(0) === "/") {
    return SEO_SITE_ORIGIN + normalized;
  }
  return SEO_SITE_ORIGIN + "/" + normalized;
}

function buildCanonical(path) {
  const baseUrl = getBaseUrl();
  const normalized = trimText(path);
  if (!normalized || normalized === "/") {
    return baseUrl + "/";
  }
  if (/^https?:\/\//i.test(normalized)) {
    return normalized;
  }
  return baseUrl + (normalized.charAt(0) === "/" ? normalized : "/" + normalized);
}

function findMetaTag(attr, value) {
  return document.head.querySelector(`meta[${attr}="${value}"]`);
}

function upsertMetaTag(attr, value, content) {
  let node = findMetaTag(attr, value);
  if (!content) {
    if (node && node.getAttribute(SEO_MANAGED_ATTR) === "true") {
      node.parentNode.removeChild(node);
    }
    return;
  }
  if (!node) {
    node = document.createElement("meta");
    node.setAttribute(attr, value);
    document.head.appendChild(node);
  }
  node.setAttribute("content", content);
  node.setAttribute(SEO_MANAGED_ATTR, "true");
}

function upsertCanonical(url) {
  let node = document.head.querySelector('link[rel="canonical"]');
  if (!url) {
    if (node && node.getAttribute(SEO_MANAGED_ATTR) === "true") {
      node.parentNode.removeChild(node);
    }
    return;
  }
  if (!node) {
    node = document.createElement("link");
    node.setAttribute("rel", "canonical");
    document.head.appendChild(node);
  }
  node.setAttribute("href", url);
  node.setAttribute(SEO_MANAGED_ATTR, "true");
}

function upsertJsonLd(payload) {
  let node = document.getElementById(SEO_JSONLD_ID);
  if (!payload) {
    if (node) {
      node.parentNode.removeChild(node);
    }
    return;
  }
  if (!node) {
    node = document.createElement("script");
    node.type = "application/ld+json";
    node.id = SEO_JSONLD_ID;
    node.setAttribute(SEO_MANAGED_ATTR, "true");
    document.head.appendChild(node);
  }
  node.textContent = JSON.stringify(payload);
}

function buildMetaPayload(meta) {
  return {
    title: limitText(meta.title || SEO_DEFAULT_TITLE, 70),
    description: limitText(meta.description || SEO_DEFAULT_DESCRIPTION, 160),
    keywords: trimText(meta.keywords || SEO_DEFAULT_KEYWORDS),
    robots: trimText(meta.robots || "index,follow"),
    canonical: trimText(meta.canonical || buildCanonical("/")),
    image: toAbsoluteUrl(meta.image || getDefaultImage()),
    ogType: trimText(meta.ogType || "website"),
    twitterCard: trimText(meta.twitterCard || "summary_large_image"),
    jsonLd: meta.jsonLd || null
  };
}

export function setSeo(meta) {
  if (typeof document === "undefined") {
    return;
  }
  const payload = buildMetaPayload(meta || {});
  document.title = payload.title;
  upsertMetaTag("name", "description", payload.description);
  upsertMetaTag("name", "keywords", payload.keywords);
  upsertMetaTag("name", "robots", payload.robots);
  upsertMetaTag("property", "og:site_name", SEO_SITE_NAME);
  upsertMetaTag("property", "og:locale", "zh_CN");
  upsertMetaTag("property", "og:type", payload.ogType);
  upsertMetaTag("property", "og:title", payload.title);
  upsertMetaTag("property", "og:description", payload.description);
  upsertMetaTag("property", "og:url", payload.canonical);
  upsertMetaTag("property", "og:image", payload.image);
  upsertMetaTag("name", "twitter:card", payload.twitterCard);
  upsertMetaTag("name", "twitter:title", payload.title);
  upsertMetaTag("name", "twitter:description", payload.description);
  upsertMetaTag("name", "twitter:image", payload.image);
  upsertCanonical(payload.canonical);
  upsertJsonLd(payload.jsonLd);
}

export function resetSeo() {
  setSeo(buildHomeSeo());
}

function buildOrganizationSchema(image) {
  return {
    "@type": "Organization",
    name: SEO_SITE_NAME,
    url: buildCanonical("/"),
    logo: image,
    image: image
  };
}

export function buildHomeSeo(data) {
  const image = resolveHomeImage(data);
  const canonical = buildCanonical("/");
  return {
    title: SEO_DEFAULT_TITLE,
    description: SEO_DEFAULT_DESCRIPTION,
    keywords: SEO_DEFAULT_KEYWORDS,
    robots: "index,follow",
    canonical: canonical,
    image: image,
    ogType: "website",
    twitterCard: "summary_large_image",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          name: SEO_SITE_NAME,
          url: canonical,
          description: SEO_DEFAULT_DESCRIPTION,
          image: image,
          inLanguage: "zh-CN"
        },
        buildOrganizationSchema(image)
      ]
    }
  };
}

function getDetailTypeLabel(temp) {
  const typeMap = {
    1: "练习",
    2: "每日一练",
    3: "问卷",
    4: "有奖答题"
  };
  return typeMap[temp] || "答题活动";
}

function getDetailStatusLabel(info) {
  if (!info) {
    return "";
  }
  if (info.is_audit === 1) {
    return "审核中";
  }
  if (info.is_violation === 1) {
    return "违规";
  }
  if (info.is_violation === 2) {
    return "争议";
  }
  return "";
}

function buildDetailSummary(info) {
  if (!info) {
    return "立吉答题，让在线学、练、考更容易。查看该答题活动的详情介绍、参与规则与活动信息。";
  }
  const title = trimText(info.title);
  const typeLabel = getDetailTypeLabel(info.temp);
  const parts = [title || "该活动", typeLabel + "活动"];
  if (info.ques_number) {
    parts.push("共" + info.ques_number + "题");
  }
  if (info.temp != 3 && info.full_score) {
    parts.push("总分" + info.full_score);
  }
  if (info.time_limit) {
    parts.push("限时" + info.time_limit + "分钟");
  } else {
    parts.push("不限时");
  }
  const joinedCount =
    info.total_join_limit > -1 || info.total_join ? info.total_join : info.joined;
  if (joinedCount || joinedCount === 0) {
    parts.push("已有" + (joinedCount || 0) + "次参与");
  }
  if (info.startDate && info.endDate) {
    parts.push("活动时间" + info.startDate + "至" + info.endDate);
  }
  return (
    parts.join("，") +
    "。支持在线查看活动详情、答题规则与参与方式，让在线学、练、考更容易。"
  );
}

function buildDetailStatusDescription(info) {
  const title = trimText(info && info.title);
  const prefix = title ? title + "：" : "";
  if (info && info.is_audit === 1) {
    return (
      prefix + "该活动正在审核中，暂时无法查看完整内容或参与。立吉答题支持线上考试、企业培训与问卷调查等场景。"
    );
  }
  if (info && info.is_violation === 1) {
    return prefix + "该活动因违规内容暂时无法访问。";
  }
  if (info && info.is_violation === 2) {
    return prefix + "该活动存在争议内容，正在处理中。";
  }
  return buildDetailSummary(info);
}

function buildDetailKeywords(info) {
  const keywords = [SEO_SITE_NAME, "在线答题", "答题活动", getDetailTypeLabel(info && info.temp)];
  const title = trimText(info && info.title);
  if (title) {
    keywords.push(title);
  }
  return keywords.join(",");
}

function buildDetailGraph(info, canonical, description, image) {
  const title = trimText(info && info.title) || "答题详情";
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        name: title,
        headline: title,
        description: description,
        url: canonical,
        image: image,
        inLanguage: "zh-CN",
        isPartOf: {
          "@type": "WebSite",
          name: SEO_SITE_NAME,
          url: buildCanonical("/")
        }
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "首页",
            item: buildCanonical("/")
          },
          {
            "@type": "ListItem",
            position: 2,
            name: title,
            item: canonical
          }
        ]
      }
    ]
  };
}

export function buildDetailLoadingSeo(qid) {
  const canonical = qid
    ? buildCanonical("/normal/detail?qid=" + encodeURIComponent(qid))
    : buildCanonical("/normal/detail");
  return {
    title: "活动详情 - " + SEO_SITE_NAME,
    description:
      "立吉答题，让在线学、练、考更容易。查看该活动的详情介绍、答题规则与参与方式。",
    keywords: SEO_DEFAULT_KEYWORDS,
    robots: "index,follow",
    canonical: canonical,
    image: getDefaultImage(),
    ogType: "article",
    twitterCard: "summary_large_image",
    jsonLd: buildDetailGraph(null, canonical, "查看答题活动详情。", getDefaultImage())
  };
}

export function buildDetailSeo(qid, info) {
  const canonical = buildCanonical(
    "/normal/detail?qid=" + encodeURIComponent(qid || "")
  );
  const titleText = trimText(info && info.title) || "答题详情";
  const statusLabel = getDetailStatusLabel(info);
  const description = limitText(
    statusLabel ? buildDetailStatusDescription(info) : buildDetailSummary(info),
    160
  );
  const title =
    (statusLabel ? "【" + statusLabel + "】" : "") +
    titleText +
    " - 立吉答题";
  const image = toAbsoluteUrl((info && info.banner) || getDefaultImage()) || getDefaultImage();
  return {
    title: title,
    description: description,
    keywords: buildDetailKeywords(info),
    robots: "index,follow",
    canonical: canonical,
    image: image,
    ogType: "article",
    twitterCard: "summary_large_image",
    jsonLd: buildDetailGraph(info, canonical, description, image)
  };
}

export function buildDetailMissingSeo(qid, lostData) {
  const canonical = qid
    ? buildCanonical("/normal/detail?qid=" + encodeURIComponent(qid))
    : buildCanonical("/normal/detail");
  const description = lostData && lostData.deleted
    ? "该活动内容已被删除或下线，当前无法访问。"
    : "该活动暂时无法访问，可能已下线或不存在。";
  return {
    title: "内容不存在或已下线 - " + SEO_SITE_NAME,
    description: description,
    keywords: SEO_DEFAULT_KEYWORDS,
    robots: "noindex,follow",
    canonical: canonical,
    image: getDefaultImage(),
    ogType: "website",
    twitterCard: "summary_large_image",
    jsonLd: buildDetailGraph(
      { title: "内容不存在或已下线" },
      canonical,
      description,
      getDefaultImage()
    )
  };
}

function resolveHomeImage(data) {
  const banners = data && data.banner;
  if (banners && banners.length && banners[0] && banners[0].index_banner) {
    return toAbsoluteUrl(banners[0].index_banner);
  }
  return getDefaultImage();
}

export {
  buildCanonical,
  getBaseUrl,
  getDetailTypeLabel,
  toAbsoluteUrl,
  META_DEFINITIONS
};



// WEBPACK FOOTER //
// ./src/utils/seo.js